import React, { useCallback, useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import Layout from "@/components/Layout";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { ArrowLeft, PencilSimple } from "@phosphor-icons/react";
import ProjectScopePanel from "@/components/rd/ProjectScopePanel";
import BriefPanel from "@/components/rd/BriefPanel";
import RevisionsPanel from "@/components/rd/RevisionsPanel";
import TestsPanel from "@/components/rd/TestsPanel";
import StudiesPanel from "@/components/rd/StudiesPanel";
import DecisionsPanel from "@/components/rd/DecisionsPanel";
import AnalyzePanel from "@/components/rd/AnalyzePanel";
import { ErrorBox, errMsg, fmtDate } from "@/components/rd/shared";

const STEPS = [
  ["rd-scope", "Scope"], ["rd-brief", "Technical brief"], ["rd-revisions", "Formula history"], ["rd-tests", "Analytical"],
  ["rd-studies", "Sensory"], ["rd-decisions", "Decisions"], ["rd-analyze", "AI review"],
];
const IDS = STEPS.map(([a]) => a);
const readHash = () => {
  const h = window.location.hash.replace("#", "");
  return IDS.includes(h) ? h : IDS[0];
};

export default function RDWorkspace() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [refreshing, setRefreshing] = useState(false);

  const load = useCallback(async () => {
    setRefreshing(true); setError("");
    try {
      const { data: d } = await api.get(`/rd/recipes/${id}`);
      setData(d);
    } catch (err) { setError(errMsg(err)); }
    finally { setRefreshing(false); }
  }, [id]);

  useEffect(() => { load(); }, [load]);

  // One section visible at a time, addressed by URL hash (#rd-tests etc.).
  // Browser back/forward fire hashchange, so history works naturally.
  const [active, setActive] = useState(readHash);
  useEffect(() => {
    const h = () => setActive(readHash());
    window.addEventListener("hashchange", h);
    return () => window.removeEventListener("hashchange", h);
  }, []);
  const go = (anchor) => {
    if (window.location.hash !== `#${anchor}`) window.location.hash = anchor;
    setActive(anchor);
  };
  const onTabKey = (e, i) => {
    let n = null;
    if (e.key === "ArrowRight") n = (i + 1) % IDS.length;
    if (e.key === "ArrowLeft") n = (i - 1 + IDS.length) % IDS.length;
    if (e.key === "Home") n = 0;
    if (e.key === "End") n = IDS.length - 1;
    if (n == null) return;
    e.preventDefault(); go(IDS[n]);
    document.getElementById(`tab-${IDS[n]}`)?.focus();
  };

  if (!data) {
    return (
      <Layout>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
          {error ? (
            <ErrorBox message={`Could not load the R&D workspace. ${error}`} onRetry={load} testId="error-rd-load" />
          ) : (
            <div className="space-y-4 animate-pulse" data-testid="rd-loading">
              <div className="h-8 w-72 bg-stone-200 rounded" />
              <div className="h-40 bg-stone-100 rounded" />
              <div className="h-64 bg-stone-100 rounded" />
            </div>
          )}
        </div>
      </Layout>
    );
  }

  const { recipe, project, revisions = [], tests = [], studies = [], decisions = [], stages = [], ai_reviews = [], scope = null, documents = [] } = data;
  const current = revisions.find((r) => r.id === recipe.current_revision_id);
  const shared = { recipeId: id, recipe, revisions, onReload: load };

  return (
    <Layout>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10" data-testid="rd-workspace">
        <div className="flex items-start justify-between gap-4 flex-wrap mb-6">
          <div>
            <Link to="/dashboard" className="text-xs text-stone-500 hover:text-stone-800 inline-flex items-center gap-1" data-testid="link-dashboard">
              <ArrowLeft size={12} /> Dashboard
            </Link>
            <div className="text-xs uppercase tracking-[0.2em] text-stone-500 mt-2">R&amp;D Workspace</div>
            <h1 className="text-3xl sm:text-4xl font-bold tracking-tight mt-1" data-testid="text-rd-recipe-name">{recipe.name}</h1>
            <div className="text-xs text-stone-500 mt-2 flex gap-2 flex-wrap items-center">
              <span className="font-mono bg-stone-100 text-stone-700 px-1.5 py-0.5 rounded" data-testid="text-current-revision">
                {current ? `Current: Rev ${current.number}` : "No saved revision"}
              </span>
              <span>Updated {fmtDate(recipe.updated_at)}</span>
              {refreshing && <span className="text-stone-400">Refreshing…</span>}
            </div>
          </div>
          <Button onClick={() => navigate(`/builder/${id}`)} className="bg-[#17324D] hover:bg-[#22476B] text-white" data-testid="button-open-builder">
            <PencilSimple size={15} className="mr-2" /> Open in Builder
          </Button>
        </div>

        {error && <div className="mb-4"><ErrorBox message={`Refresh failed: ${error}`} onRetry={load} /></div>}

        <div role="tablist" aria-label="Workspace sections" className="flex gap-1 overflow-x-auto mb-6 border-b border-stone-200 -mx-4 px-4 sm:mx-0 sm:px-0">
          {STEPS.map(([anchor, label], i) => {
            const on = active === anchor;
            return (
              <a key={anchor} id={`tab-${anchor}`} href={`#${anchor}`} role="tab" aria-selected={on} aria-controls={`panel-${anchor}`}
                tabIndex={on ? 0 : -1} onKeyDown={(e) => onTabKey(e, i)}
                onClick={(e) => { e.preventDefault(); go(anchor); }}
                className={`shrink-0 text-sm px-3 py-2 -mb-px border-b-2 whitespace-nowrap ${on ? "border-[#17324D] text-[#17324D] font-semibold" : "border-transparent text-[#5B6570] hover:text-[#17324D]"}`}
                data-testid={`link-step-${anchor}`}>
                {label}
              </a>
            );
          })}
        </div>

        <div className="max-w-4xl">
          {[
            ["rd-scope", <ProjectScopePanel recipeId={id} scope={scope} documents={documents} onSaved={load} />],
            ["rd-brief", <BriefPanel recipeId={id} project={project} onSaved={load} />],
            ["rd-revisions", <RevisionsPanel key={revisions[0]?.id || "none"} {...shared} />],
            ["rd-tests", <TestsPanel {...shared} tests={tests} targets={project?.targets || []} />],
            ["rd-studies", <StudiesPanel {...shared} studies={studies} />],
            ["rd-decisions", <DecisionsPanel {...shared} decisions={decisions} stages={stages} tests={tests} />],
            ["rd-analyze", <AnalyzePanel {...shared} reviews={ai_reviews} />],
          ].map(([anchor, el]) => (
            // Inactive sections stay mounted (hidden) so unsaved form input survives tab switches.
            <div key={anchor} id={`panel-${anchor}`} role="tabpanel" aria-labelledby={`tab-${anchor}`} hidden={active !== anchor}>
              {el}
            </div>
          ))}
        </div>
      </div>
    </Layout>
  );
}
